import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Pencil, Trash2, Search, MoreHorizontal, Eye, Car, FileOutput, Wrench } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import TablePagination from "../components/TablePagination";
import FilterChips from "../components/FilterChips";
import { VehicleFormDialog } from "../components/VehicleFormDialog";
import { VehicleStatusDialog } from "../components/VehicleStatusDialog";
import { fmtDate } from "../lib/format";

const STATUS = {
  tersedia: { label: "Tersedia", cls: "bg-[#198754] hover:bg-[#198754]" },
  digunakan: { label: "Digunakan", cls: "bg-[#10056E] hover:bg-[#10056E]" },
  penyelenggaraan: { label: "Penyelenggaraan", cls: "bg-sky-600 hover:bg-sky-600" },
  dilupus: { label: "Dilupus", cls: "bg-[#DC3545] hover:bg-[#DC3545]" },
};
export default function Vehicles() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canManage = user?.role === "admin" || user?.role === "operator";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [units, setUnits] = useState([]);
  const [categories, setCategories] = useState([]);
  const [unitFilter, setUnitFilter] = useState("semua");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("semua");
  const [categoryFilter, setCategoryFilter] = useState("semua");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [statusFor, setStatusFor] = useState(null);
  const catImages = useMemo(() => {
    const m = {};
    categories.forEach((c) => { if (c.image) m[c.name] = c.image; });
    return m;
  }, [categories]);

  const load = useCallback(() => api.get("/vehicles").then((r) => setVehicles(r.data)), []);
  useEffect(() => {
    load();
    api.get("/units").then((r) => setUnits(r.data));
    api.get("/vehicle-categories").then((r) => setCategories(r.data));
  }, [load]);
  useEffect(() => {
    if (searchParams.get("new") === "1" && isAdmin) setOpen(true);
  }, [searchParams, isAdmin]);
  useEffect(() => { setPage(1); }, [search, statusFilter, categoryFilter]);

  const filtered = vehicles.filter((v) => {
    const q = search.toLowerCase();
    const matches = !q || `${v.registration_number} ${v.make} ${v.model} ${v.vehicle_type}`.toLowerCase().includes(q);
    return matches && (statusFilter === "semua" || v.status === statusFilter) && (categoryFilter === "semua" || v.category === categoryFilter) && (unitFilter === "semua" || v.unit_id === unitFilter);
  });
  const paged = filtered.slice((page - 1) * perPage, page * perPage);

  const remove = async (id) => {
    if (!window.confirm("Padam kenderaan ini?")) return;
    try {
      await api.delete(`/vehicles/${id}`);
      toast.success("Kenderaan dipadam");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="vehicles-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Kenderaan</h1>
          <p className="text-sm text-muted-foreground mt-1">Daftar dan urus kenderaan fleet agensi — {vehicles.length} kenderaan berdaftar.</p>
        </div>
        {canManage && (
          <>
            {isAdmin && (
              <Button className="bg-[#10056E] hover:bg-[#2a17a8]" data-testid="add-vehicle-btn"
                onClick={() => { setEditing(null); setOpen(true); }}>
                <Plus className="h-4 w-4 mr-2" /> Daftar Kenderaan
              </Button>
            )}
            <VehicleFormDialog open={open} vehicle={editing} onSaved={load}
              onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }} />
            <VehicleStatusDialog vehicle={statusFor} open={!!statusFor} onOpenChange={(o) => !o && setStatusFor(null)} onDone={load} />
          </>
        )}
      </div>

      <div>
        <p className="text-sm font-semibold mb-3">Kategori Kenderaan</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          {categories.map((c) => {
            const count = vehicles.filter((v) => v.category === c.name).length;
            const active = categoryFilter === c.name;
            return (
              <button
                key={c.name}
                type="button"
                onClick={() => setCategoryFilter(active ? "semua" : c.name)}
                data-testid={`category-card-${c.name}`}
                className={`stat-card bg-white border rounded-xl p-3 text-center transition-colors ${
                  active ? "border-[#10056E] ring-1 ring-[#10056E]" : "border-slate-200 hover:border-[#10056E]/50"
                }`}
              >
                {c.image ? (
                  <img src={c.image} alt={c.name} className="h-12 w-full object-contain" />
                ) : (
                  <div className="h-12 flex items-center justify-center">
                    <Car className="h-8 w-8 text-slate-300" />
                  </div>
                )}
                <p className="text-xs font-semibold mt-2 truncate">{c.name}</p>
                <p className="text-[10px] text-muted-foreground">{count} kenderaan</p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b flex flex-wrap items-center justify-between gap-3">
          <FilterChips
            value={statusFilter}
            onChange={setStatusFilter}
            testId="vehicle-status-chips"
            options={[
              { value: "semua", label: "Semua", count: vehicles.length },
              ...Object.entries(STATUS).map(([k, v]) => ({ value: k, label: v.label, count: vehicles.filter((x) => x.status === k).length })),
            ]}
          />
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-64">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9 h-9" placeholder="Cari no. pendaftaran, jenama..." value={search}
                onChange={(e) => setSearch(e.target.value)} data-testid="vehicle-search-input" />
            </div>
            <Select value={unitFilter} onValueChange={setUnitFilter}>
              <SelectTrigger className="w-52 h-9" data-testid="vehicle-unit-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white max-h-64">
                <SelectItem value="semua">Semua Bahagian/Unit</SelectItem>
                {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-44 h-9" data-testid="vehicle-category-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="semua">Semua Kategori</SelectItem>
                {categories.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/70">
              <TableHead className="w-20">Foto</TableHead>
              <TableHead>No. Pendaftaran</TableHead>
              <TableHead>Kenderaan</TableHead>
              <TableHead>Bahagian/Unit</TableHead>
              <TableHead>Kategori</TableHead>
              <TableHead>Bahan Api</TableHead>
              <TableHead>Odometer (KM)</TableHead>
              <TableHead>Didaftar</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Tindakan</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.length === 0 && (
              <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-10">Tiada kenderaan dijumpai.</TableCell></TableRow>
            )}
            {paged.map((v) => (
              <TableRow key={v.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/kenderaan/${v.id}`)} data-testid={`vehicle-row-${v.registration_number}`}>
                <TableCell data-testid={`vehicle-thumb-${v.registration_number}`}>
                  {v.photo_thumb ? (
                    <img src={v.photo_thumb} alt={v.registration_number} className="h-10 w-14 rounded-md object-cover border" loading="lazy" />
                  ) : (
                    <span className="h-10 w-14 rounded-md bg-slate-100 border flex items-center justify-center text-slate-400"><Car className="h-4 w-4" /></span>
                  )}
                </TableCell>
                <TableCell className="font-semibold">{v.registration_number}</TableCell>
                <TableCell>{v.make} {v.model} <span className="text-muted-foreground text-xs">({v.vehicle_type})</span></TableCell>
                <TableCell className="text-sm">{v.unit_name || "-"}</TableCell>
                <TableCell className="text-sm">{v.category}</TableCell>
                <TableCell className="text-sm">{v.fuel_type}</TableCell>
                <TableCell className="text-sm">{Number(v.current_odometer).toLocaleString()}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{fmtDate(v.created_at)}</TableCell>
                <TableCell><Badge className={STATUS[v.status]?.cls}>{STATUS[v.status]?.label}</Badge></TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" onClick={(e) => e.stopPropagation()} data-testid={`vehicle-actions-${v.registration_number}`}>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-white w-48">
                      <DropdownMenuItem className="cursor-pointer" onClick={() => navigate(`/kenderaan/${v.id}`)} data-testid={`vehicle-view-${v.registration_number}`}>
                        <Eye className="h-4 w-4 mr-2" /> Lihat Detail
                      </DropdownMenuItem>
                      {canManage && (
                        <DropdownMenuItem className="cursor-pointer text-amber-700" data-testid={`vehicle-status-${v.registration_number}`}
                          onClick={() => setStatusFor(v)}>
                          <Wrench className="h-4 w-4 mr-2" /> Tukar Status
                        </DropdownMenuItem>
                      )}
                      {canManage && (
                        <DropdownMenuItem className="cursor-pointer text-[#10056E]" data-testid={`vehicle-jana-laporan-${v.registration_number}`}
                          onClick={() => navigate(`/jana-laporan?vehicle=${v.id}`)}>
                          <FileOutput className="h-4 w-4 mr-2" /> Jana Laporan
                        </DropdownMenuItem>
                      )}
                      {canManage && (
                        <DropdownMenuItem className="cursor-pointer" data-testid={`vehicle-edit-${v.registration_number}`}
                          onClick={() => { setEditing(v); setOpen(true); }}>
                          <Pencil className="h-4 w-4 mr-2" /> Kemas Kini
                        </DropdownMenuItem>
                      )}
                      {isAdmin && (
                        <DropdownMenuItem className="cursor-pointer text-[#DC3545]" data-testid={`vehicle-delete-${v.registration_number}`}
                          onClick={() => remove(v.id)}>
                          <Trash2 className="h-4 w-4 mr-2" /> Padam
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination page={page} setPage={setPage} perPage={perPage} setPerPage={setPerPage} total={filtered.length} />
      </div>
    </div>
  );
}
